import {
    TB_COLUMN_FIELDS as SHARED_TB_COLUMN_FIELDS,
    GL_COLUMN_FIELDS as SHARED_GL_COLUMN_FIELDS,
    type TColumnMappingFieldConfig,
} from "@/ui/organisms/OrganismColumnMappings/columnFieldConfigs";

/**
 * The files LaSalle's business partners send, as the LaSalle backend reads them.
 * Mirrors `ingestion/file_processing/file_type_columns.py`,
 * `mappings/coa_upload/parser.py` and `mappings/propcode_mapping/parser.py` in
 * `src/proda_finance/modules/lasalle` — keep in sync.
 */

/** Level of every LaSalle CoA and propcode mapping: the mapping files have no Level column. */
export const MAPPING_LEVEL = "P";

// LaSalle deals have no segments and its business partners map one account to
// one Yardi account, so neither the counterparty-code columns nor the second
// source-account column is ever mapped.
const lasalleFields = (fields: TColumnMappingFieldConfig[]): TColumnMappingFieldConfig[] =>
    fields.filter((field) => !field.conditionalSegment && !field.multiCriteriaOnly);

/**
 * TB columns. The TB has no property column: its lines take the property of the
 * GL for the same deal and period.
 */
export const TB_COLUMN_FIELDS: TColumnMappingFieldConfig[] = lasalleFields(
    SHARED_TB_COLUMN_FIELDS,
).filter((field) => field.fieldType !== "local_propcode");

/**
 * Shown while the TB's Opening Balance is unmapped. It is optional, but without it
 * the comparison has no PM beginning balances, so its Beginning Check never runs.
 */
export const OPENING_BALANCE_UNMAPPED_NOTE =
    "Opening Balance is not mapped. The TB uploads, but the comparison will have no PM " +
    "beginning balances, so its Beginning Check can't compare them with Yardi's.";

/** Whether a deal's saved TB column mappings leave Opening Balance unmapped. */
export function isOpeningBalanceUnmapped(
    mappings: ReadonlyArray<{ fieldType: string; columnName: string; isActive: boolean }>,
): boolean {
    return !mappings.some(
        (mapping) =>
            mapping.fieldType === "opening_balance" &&
            mapping.isActive &&
            mapping.columnName !== "",
    );
}

export const GL_COLUMN_FIELDS: TColumnMappingFieldConfig[] = lasalleFields(SHARED_GL_COLUMN_FIELDS);

/**
 * CoA mapping mode of every LaSalle business partner: the GL Account Mapping
 * file maps one account to one Yardi account (the backend refuses multi-criteria).
 */
export const COA_MAPPING_MODE = "single";

/** Columns of the GL Account Mapping file: a business partner's CoA mappings. */
export const COA_MAPPING_COLUMNS = [
    "PM Account",
    "PM Acc Description",
    "Yardi Account",
    "Yardi Acc Description",
] as const;

/** One CoA mapping as a row of the GL Account Mapping file. */
export function coaMappingFileRow(mapping: {
    thirdAccountCode: string;
    thirdAccountName: string;
    yardiAccountCode: string | null;
    yardiAccountName: string | null;
}): Record<string, string> {
    return {
        "PM Account": mapping.thirdAccountCode,
        "PM Acc Description": mapping.thirdAccountName,
        "Yardi Account": mapping.yardiAccountCode ?? "",
        "Yardi Acc Description": mapping.yardiAccountName ?? "",
    };
}

/** Columns of the Property Code Mapping file: a deal's propcode mappings. */
export const PROPCODE_MAPPING_COLUMNS = ["PM Property Code", "Yardi Property Code"] as const;

/** One propcode mapping as a row of the Property Code Mapping file. */
export function propcodeMappingFileRow(
    sourcePropcode: string,
    targetPropcode: string,
): Record<string, string> {
    return {
        "PM Property Code": sourcePropcode,
        "Yardi Property Code": targetPropcode,
    };
}
